type ResponseLike = {
  headers?: {get?: (name: string) => string | null};
  redirected?: boolean;
  status?: number;
  type?: string;
};

type ErrorLike = {
  bodyText?: unknown;
  name?: unknown;
  response?: unknown;
  statusCode?: unknown;
};

type ServerParseErrorBodyShape =
  | 'empty'
  | 'html-like'
  | 'json-like'
  | 'other'
  | 'unavailable';

type ServerParseErrorContext = ResponseContext & {
  bodyCharacterCount?: number;
  bodyShape: ServerParseErrorBodyShape;
};

type ResponseContext = {
  contentEncoding?: string;
  contentLength?: string;
  contentType?: string;
  responseRedirected?: boolean;
  responseType?: string;
  statusCode?: number;
};

export function getServerParseErrorContext(
  error: unknown
): ServerParseErrorContext | null {
  if (getErrorName(error) !== 'ServerParseError') {
    return null;
  }

  const parseError = error as ErrorLike;
  const bodyText =
    typeof parseError.bodyText === 'string' ? parseError.bodyText : undefined;

  // Classify the body for diagnosis without returning its contents to telemetry.
  return {
    bodyCharacterCount: bodyText?.length,
    bodyShape: getBodyShape(bodyText),
    ...getResponseContext(parseError.response, parseError.statusCode),
  };
}

function getResponseContext(
  responseValue: unknown,
  fallbackStatusCode: unknown
): ResponseContext {
  const response = isResponseLike(responseValue) ? responseValue : undefined;
  const statusCode =
    typeof response?.status === 'number'
      ? response.status
      : typeof fallbackStatusCode === 'number'
        ? fallbackStatusCode
        : undefined;

  return {
    contentEncoding: getHeader(response, 'content-encoding'),
    contentLength: getHeader(response, 'content-length'),
    contentType: getHeader(response, 'content-type'),
    responseRedirected:
      typeof response?.redirected === 'boolean'
        ? response.redirected
        : undefined,
    responseType:
      typeof response?.type === 'string' ? response.type : undefined,
    statusCode,
  };
}

function getErrorName(error: unknown): string | undefined {
  if (error == null || typeof error !== 'object') {
    return undefined;
  }
  const name = (error as ErrorLike).name;
  return typeof name === 'string' ? name : undefined;
}

function isResponseLike(response: unknown): response is ResponseLike {
  return response != null && typeof response === 'object';
}

function getBodyShape(bodyText: string | undefined): ServerParseErrorBodyShape {
  if (bodyText == null) {
    return 'unavailable';
  }
  if (bodyText.length === 0) {
    return 'empty';
  }

  const firstCharacter = bodyText.trimStart()[0];
  if (firstCharacter === '<') {
    return 'html-like';
  }
  if (firstCharacter === '{' || firstCharacter === '[') {
    return 'json-like';
  }
  return 'other';
}

function getHeader(
  response: ResponseLike | undefined,
  name: string
): string | undefined {
  const value = response?.headers?.get?.(name);
  return value ?? undefined;
}
